import React, { useState, useRef, useEffect } from "react";
import {
  GoogleMap,
  Autocomplete,
  Marker,
  OverlayView,
} from "@react-google-maps/api";
import {
  updateMyAddress,
  getAddressData,
} from "../../redux/reducers/myAddress";
import * as CUSTOM from "../../utils/helper/custom";
import { Img } from "components";
import { CloseSVG } from "assets/images";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { setSnackbar } from "../../redux/reducers/snackbar";

let timer = null;

// Haversine formula helpers to calculate distance (in km) between two lat/lng points
const deg2rad = (deg) => deg * (Math.PI / 180);
const getDistanceFromLatLonInKm = (lat1, lon1, lat2, lon2) => {
  const R = 6371; // Radius of the earth in km
  const dLat = deg2rad(lat2 - lat1);
  const dLon = deg2rad(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(deg2rad(lat1)) *
      Math.cos(deg2rad(lat2)) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c; // Distance in km
};

function MyMapComponent() {
  let addressData = useSelector(getAddressData);
  const { t } = useTranslation();
  const mapRef = useRef(null); // Add a mapRef to access the GoogleMap instance
  const dispatch = useDispatch();
  const [selectedPlace, setSelectedPlace] = useState(null);
  const [searchAddress, setSearchAddress] = useState("");
  const autocompleteRef = useRef(null);
  const [markerStatus, setMarkerStatus] = useState(false);
  const [infoWindowOpen, setInfoWindowOpen] = useState(true);
  const [mapHeight, setMapHeight] = useState("70vh");
  const defaultLat = Number(process.env.REACT_APP_DEFAULT_LAT);
  const defaultLng = Number(process.env.REACT_APP_DEFAULT_LNG);
  const [markerPosition, setMarkerPosition] = useState({
    lat: Number(addressData?.lattitude) || defaultLat,
    lng: Number(addressData?.longitude) || defaultLng,
  });
  const zoom =11;

  const [currentLocationPos, setCurrentLocationPos] = useState(null);
  const [locationLoading, setLocationLoading] = useState(true);
  const [locationError, setLocationError] = useState(false);
  const [distanceFromCurrent, setDistanceFromCurrent] = useState(null);
  const locationVerifiedRef = useRef(false);
  const distanceRef = useRef(null);
  const skipNextGeocodeRef = useRef(true);
  const hasUserInteracted = useRef(false);

  const handlePlaceSelect = () => {
    if (autocompleteRef.current) {
      const place = autocompleteRef.current.getPlace();
      setSelectedPlace(place);
      hasUserInteracted.current = true;
      setInfoWindowOpen(true);
    }
  };

  const handleMarkerDrag = (event) => {
    hasUserInteracted.current = true;
    const newLat = event.latLng.lat();
    const newLng = event.latLng.lng();
    setMarkerPosition({
      lat: newLat,
      lng: newLng,
    });
    setMarkerStatus(false);
    reverseGeocode(newLat, newLng);
  };

  const handleBoundsChanged = (e) => {
    if (!hasUserInteracted.current) return;
    if (mapRef.current && markerStatus == false) {
      const newCenter = mapRef.current.getCenter();
      if (newCenter) {
        if (newCenter.lat() != markerPosition.lat) {
          clearTimeout(timer);
          timer = setTimeout(() => {
            setMarkerPosition({
              lat: newCenter.lat(),
              lng: newCenter.lng(),
            });
          }, 300);
        }
      }
    }
  };

  const reverseGeocode = (lat, lng) => {
    const apiKey = process.env.REACT_APP_GOOGLE_API_KEY;
    const geocodingUrl = `https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${lng}&key=${apiKey}`;
    fetch(geocodingUrl)
      .then((response) => response.json())
      .then((data) => {
        if (data.status === "OK") {
          let place = data.results[0];
          const addressComponents = place.address_components;
          let streetNumber = "";
          let apartmentNumber = "";
          let city = "";
          let state = "";
          let country = "";
          let postalCode = "";
          for (const component of addressComponents) {
            const types = component.types;
            if (types.includes("street_number")) {
              streetNumber = component.long_name;
            }
            if (types.includes("subpremise")) {
              apartmentNumber = component.long_name;
            }
            if (types.includes("locality")) {
              city = component.long_name;
            }
            if (types.includes("administrative_area_level_1")) {
              state = component.long_name;
            }
            if (types.includes("country")) {
              country = component.long_name;
            }
            if (types.includes("postal_code")) {
              postalCode = component.long_name;
            }
          }
          dispatch(
            updateMyAddress({
              ...addressData,
              formatted_address: place.formatted_address,
              street_address: place.formatted_address,
              lattitude: lat,
              // address: place.formatted_address,
              address: "",
              longitude: lng,
              city: city,
              building_number: streetNumber,
              state: state,
              postal_code: postalCode,
              country: country,
              floor_apartment: apartmentNumber,
              street_number: streetNumber,
              location_verified: locationVerifiedRef.current,
              current_location_distance: distanceRef.current,
            })
          );
          setInfoWindowOpen(true);
        }
      });
  };

  const setCurrentLocation = async () => {
    try {
      let latlng = await CUSTOM.currentLocation();
      if (latlng?.lat && latlng?.lng) {
        hasUserInteracted.current = true;
        const newPos = {
          lat: Number(latlng.lat),
          lng: Number(latlng.lng),
        };
        setMarkerPosition(newPos);
        setCurrentLocationPos(newPos);
        locationVerifiedRef.current = true;
        setInfoWindowOpen(true);
        if (mapRef.current) {
          mapRef.current.panTo(newPos);
        }
        reverseGeocode(newPos.lat, newPos.lng);
        dispatch(
          updateMyAddress({
            ...addressData,
            location_verified: true,
            location_skipped: false,
          })
        );
      } else {
        dispatch(
          setSnackbar({
            snackbarOpen: true,
            snackbarMessage: t("pleaseEnableYourCurrentLocation"),
            snackbarState: "error",
          })
        );
      }
    } catch (error) {
      dispatch(
        setSnackbar({
          snackbarOpen: true,
          snackbarMessage: t("pleaseEnableYourCurrentLocation"),
          snackbarState: "error",
        })
      );
    }
  };

  // function to (re)request current location - used for mandatory location + retry button
  const requestCurrentLocation = () => {
    setLocationLoading(true);
    setLocationError(false);
    if (!navigator.geolocation) {
      // setLocationError(true);
      setLocationLoading(false);
      locationVerifiedRef.current = false;
      const isEditingExisting = addressData?.id && Number(addressData.id) > 0;
      dispatch(
        updateMyAddress({
          ...addressData,
          location_verified: false,
          street_address: isEditingExisting ? addressData?.street_address : "",
          formatted_address: isEditingExisting ? addressData?.formatted_address : "",
          address: isEditingExisting ? addressData?.address : "",
        })
      );
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;
        hasUserInteracted.current = true;
        setCurrentLocationPos({ lat: latitude, lng: longitude });
        const isEditingExisting = addressData?.id && Number(addressData.id) > 0;
        if (!isEditingExisting) {
          setMarkerPosition({ lat: latitude, lng: longitude });
          reverseGeocode(latitude, longitude);
        }
        setLocationLoading(false);
        setLocationError(false);
        locationVerifiedRef.current = true;
        dispatch(updateMyAddress({ ...addressData, location_verified: true, location_skipped: false, }));
      },
      (error) => {
        console.log("Location access denied or unavailable", error);
        // setLocationError(true);
        setLocationLoading(false);
        locationVerifiedRef.current = false;
        setCurrentLocationPos(null);
        setDistanceFromCurrent(null);
        distanceRef.current = null;
        const isEditingExisting = addressData?.id && Number(addressData.id) > 0;
        if (!isEditingExisting) {
          setMarkerPosition({ lat: defaultLat, lng: defaultLng });
          dispatch(
            updateMyAddress({
              ...addressData,
              location_verified: false,
              current_location_distance: null,
              street_address: "",
              formatted_address: "",
              address: "",
            })
          );
        } else {
          dispatch(
            updateMyAddress({
              ...addressData,
              location_verified: false,
              current_location_distance: null,
            })
          );
        }
      }
    );
  };

  /*
  const handleSkipLocation = () => {
    setLocationLoading(false);
    setLocationError(false);
    locationVerifiedRef.current = false;
    setCurrentLocationPos(null);
    setDistanceFromCurrent(null);
    distanceRef.current = null;
    dispatch(
      updateMyAddress({
        ...addressData,
        location_verified: false,
        location_skipped: true,
        current_location_distance: null,
        street_address: "",
        formatted_address: "",
      })
    );
  };
  */

  useEffect(() => {
    const isEditingExisting = addressData?.id && Number(addressData.id) > 0;
    if (!isEditingExisting) {
      // Clear any stale street address so "Move pin" tooltip shows on default location
      dispatch(
        updateMyAddress({
          ...addressData,
          street_address: "",
          formatted_address: "",
          address: "",
        })
      );
      requestCurrentLocation();
    } else {
      setLocationLoading(false);
      if (addressData?.lattitude && addressData?.longitude) {
        setMarkerPosition({
          lat: Number(addressData.lattitude),
          lng: Number(addressData.longitude),
        });
      }
      // Silently check location permission to set location_verified correctly — do NOT move marker
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            const { latitude, longitude } = position.coords;
            setCurrentLocationPos({ lat: latitude, lng: longitude });
            locationVerifiedRef.current = true;
            dispatch(
              updateMyAddress({
                ...addressData,
                location_verified: true,
                location_skipped: false,
              })
            );
          },
          () => {
            locationVerifiedRef.current = false;
            dispatch(
              updateMyAddress({
                ...addressData,
                location_verified: false,
              })
            );
          }
        );
      } else {
        locationVerifiedRef.current = false;
        dispatch(updateMyAddress({ ...addressData, location_verified: false }));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (addressData?.id && Number(addressData.id) > 0 && addressData?.lattitude && addressData?.longitude) {
      setMarkerPosition({
        lat: Number(addressData.lattitude),
        lng: Number(addressData.longitude),
      });
    }
  }, [addressData?.id, addressData?.lattitude, addressData?.longitude]);

  useEffect(() => {
    if (mapRef.current && selectedPlace && selectedPlace.geometry) {
      const newPosition = {
        lat: selectedPlace.geometry.location.lat(),
        lng: selectedPlace.geometry.location.lng(),
      };
      setMarkerPosition(newPosition);
      if (selectedPlace.geometry.viewport) {
        mapRef.current.fitBounds(selectedPlace.geometry.viewport);
      }
    }
  }, [selectedPlace]);

  useEffect(() => {
    const isEditingExisting = addressData?.id && Number(addressData.id) > 0;
    if (isEditingExisting) return;

    if (markerPosition.lat && Number(markerPosition.lat) !== Number(addressData?.lattitude)) {
      const isDefault =
        Math.abs(Number(markerPosition.lat) - defaultLat) < 0.001 &&
        Math.abs(Number(markerPosition.lng) - defaultLng) < 0.001;
      if (!locationVerifiedRef.current && !hasUserInteracted.current && isDefault) {
        return;
      }
      reverseGeocode(Number(markerPosition.lat), Number(markerPosition.lng));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [markerPosition]);

    // compute distance between current location and marker whenever either changes
  useEffect(() => {
    if (locationVerifiedRef.current && currentLocationPos && markerPosition?.lat && markerPosition?.lng) {
      const dist = getDistanceFromLatLonInKm(
        currentLocationPos.lat,
        currentLocationPos.lng,
        markerPosition.lat,
        markerPosition.lng
      );
      setDistanceFromCurrent(dist);
      distanceRef.current = dist;
      dispatch(updateMyAddress({ ...addressData, current_location_distance: dist }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [markerPosition, currentLocationPos]);

  const handleResize = () => {
    if (window.innerWidth <= 990) {
      setMapHeight("calc(100vh - 410px)");  
    } else {
      setMapHeight("70vh");  
    }

    if (mapRef.current && window.google?.maps?.event) {
      window.google.maps.event.trigger(mapRef.current, "resize");
      mapRef.current.setCenter(markerPosition);
    }
  };

  useEffect(() => {
    handleResize();  
    window.addEventListener("resize", handleResize); 

    return () => {
      window.removeEventListener("resize", handleResize);  
    };
  }, []);

  const mapStyles = {
    width: "100%",
    height: mapHeight,
    position: "relative",
    overflow: "hidden",
    isolation: "isolate",
  };

  const isEditingExisting = addressData?.id && Number(addressData.id) > 0;
  const hasStreetAddress = Boolean(
    (addressData?.street_address && addressData.street_address.trim() !== "") ||
    (addressData?.formatted_address && addressData.formatted_address.trim() !== "") ||
    (addressData?.address && addressData.address.trim() !== "")
  );
  const showMovePinTooltip = !isEditingExisting && !hasStreetAddress;

  /*
  // block map until location permission resolved (mandatory)
    if (locationError) {
      return (
        <div
          style={{
            ...mapStyles,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            background: "#f5f5f5",
            textAlign: "center",
            padding: "20px",
          }}
        >
          <p className="text-sm mb-3">
            {t("locationAccessIsRequired")}
          </p>
          <div style={{ display: "flex", gap: "10px" }}>
            
            <button
              onClick={handleSkipLocation}
              style={{
                padding: "8px 16px",
                background: "#ffffff",
                color: "#771237",
                borderRadius: "6px",
                border: "1px solid #771237",
                cursor: "pointer",
              }}
            >
              {t("skip")}
            </button>
            <button
              onClick={requestCurrentLocation}
              style={{
                padding: "8px 16px",
                background: "#771237",
                color: "#fff",
                borderRadius: "6px",
                border: "none",
                cursor: "pointer",
              }}
            >
              {t("retry")}
            </button>
          </div>
        </div>
      );
    }
  */

  //don't show default fallback location, wait till current location is fetched
  if (locationLoading) {
    return (
      <div style={{ ...mapStyles, display: "flex", alignItems: "center", justifyContent: "center" }}>
        Loading map...
      </div>
    );
  }

  return (
    <GoogleMap
      mapContainerStyle={mapStyles}
      // center={markerPosition}
      center={markerPosition}
      zoom={zoom}
      // explicitly enable map view controls so they show on both web & mobile
      options={{
        zoomControl: true,
        mapTypeControl: false,
        fullscreenControl: true,
        streetViewControl: false,
      }}
      onLoad={(map) => {
        mapRef.current = map;
        // if (firstLoad == false && markerPosition?.lat!=process.env.REACT_APP_DEFAULT_LAT) {
        //   const geocoder = new window.google.maps.Geocoder();
        //   geocoder.geocode({ location: markerPosition }, (results, status) => {
        //     if (status === "OK" && results[0]) {
        //       let PlaceSearch = results[0];
        //       if (PlaceSearch.geometry.viewport) {
        //         mapRef.current.fitBounds(PlaceSearch.geometry.viewport);
        //       } 
        //     }
        //   });
        //   setFirstLoad(true);
        // }
        // Save the GoogleMap instance in the ref
      }}
      onBoundsChanged={(e) => {
        handleBoundsChanged(e);
      }}
      onClick={(e) => {
        handleMarkerDrag(e);
      }}
    >
      <div className="my-location-icon" onClick={(e) => {
        setCurrentLocation();
      }}></div>
      <div className="search-map-input">
        <Img
          className="cursor-pointer h-5 ltr:mr-3 rtl:ml-3 my-auto absolute top-[14px] ltr:left-[10px] rtl:right-[10px]"
          src="/images/img_search_black_900.svg"
          alt="search"
        />
        <Autocomplete
          onLoad={(autocomplete) => {
            autocompleteRef.current = autocomplete;
          }}
          options={{
            componentRestrictions: { country: "SA" },
          }}
          onPlaceChanged={handlePlaceSelect}
        >
          <input
            type="text"
            className="form-control"
            placeholder="Enter Location"
            id="google-address-search"
            onChange={(e) => {
              setSearchAddress(e.target.value);
            }}
          />
        </Autocomplete>
        {searchAddress ? (
          <CloseSVG
            fillColor="#666666"
            className="cursor-pointer h-5 my-auto absolute ltr:right-2 rtl:left-2 top-[14px]"
            height={24}
            width={24}
            viewBox="0 0 24 24"
            onClick={(e) => {
              setSearchAddress("");
              document.getElementById("google-address-search").value = "";
            }}
          />
        ) : null}
      </div>

      {markerPosition && (
        <Marker
          position={markerPosition}
          draggable={true} // Enable marker dragging
          onDragEnd={handleMarkerDrag} // Callback function for when the marker is dragged
          onDragStart={(e) => {
            setMarkerStatus(true);
            setInfoWindowOpen(true);
          }}
          onClick={() => setInfoWindowOpen(!infoWindowOpen)}
        >
          {showMovePinTooltip && (
            <OverlayView
              position={markerPosition}
              mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
              getPixelPositionOffset={() => ({ x: -110, y: -88 })}
            >
              <div
                className="custom-info-window"
                style={{
                  background: "white",
                  padding: "10px 18px",
                  borderRadius: "8px",
                  boxShadow: "0 2px 10px rgba(0, 0, 0, 0.2)",
                  width: "220px",
                  position: "relative",
                  textAlign: "center",
                  pointerEvents: "none",
                }}
              >
                <p style={{ margin: 0, fontSize: "14px", fontWeight: 600, color: "#1A1A1A" }}>
                  {t("movePinSelectLocation") || "Move pin/Select location"}
                </p>
                <div
                  style={{
                    position: "absolute",
                    bottom: "-7px",
                    left: "50%",
                    transform: "translateX(-50%)",
                    width: 0,
                    height: 0,
                    borderLeft: "7px solid transparent",
                    borderRight: "7px solid transparent",
                    borderTop: "7px solid white",
                    filter: "drop-shadow(0 2px 1px rgba(0,0,0,0.1))",
                  }}
                />
              </div>
            </OverlayView>
          )}
          {infoWindowOpen && hasStreetAddress && (
            <OverlayView
              position={markerPosition}
              mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
              getPixelPositionOffset={() => ({ x: -130, y: -110 })} // Adjust this to position the overlay
            >
              <div
                className="custom-info-window"
                style={{
                  background: "white",
                  padding: "16px 20px 16px 16px",
                  borderRadius: "8px",
                  boxShadow: "0 2px 10px rgba(0, 0, 0, 0.3)",
                  width: "260px",
                  position: "relative",
                }}
              >
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setInfoWindowOpen(false);
                  }}
                  style={{
                    position: "absolute",
                    top: "5px",
                    right: "5px",
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    fontSize: "16px",
                  }}
                >
                  ✖
                </button>
                <p className="text-sm ">{addressData?.formatted_address || addressData?.street_address}</p>
                {/* Add more details or controls as needed */}
              </div>
            </OverlayView>
          )}
        </Marker>
      )}
    </GoogleMap>
  );
}

export default MyMapComponent;
